import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Image, Input, Message, Modal, Radio, Select, Space, Table, Upload } from '@arco-design/web-react';
import { baseUrl } from '../utils';
import { Audience, Candidate, HistoryRow, Options, Page, Preview, SendRequest, SendResult,
  audienceDescription, audienceNames, memberNames, paymentNames, errorCode, errorText, pendingKey, read, requestId, write } from './notificationApi';
import './notification.scss';

export default function SystemNotifications() {
  const [options, setOptions] = useState<Options>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [audience, setAudience] = useState<Audience>({ type: 'USER' });
  const [searchBy, setSearchBy] = useState('gameName');
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<Candidate[]>([]);
  const [userPage, setUserPage] = useState(1);
  const [searched, setSearched] = useState(false);
  const [searchBusy, setSearchBusy] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Candidate>();
  const [contentType, setContentType] = useState(1);
  const [content, setContent] = useState('');
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<Preview>();
  const [previewKey, setPreviewKey] = useState('');
  const [pending, setPending] = useState<SendRequest>();
  const [storageError, setStorageError] = useState('');
  const [result, setResult] = useState<SendResult>();
  const [history, setHistory] = useState<Page<HistoryRow>>({ list: [], total: 0 });
  const [historyError, setHistoryError] = useState('');
  const [page, setPage] = useState(1);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [detail, setDetail] = useState<HistoryRow>();
  const [recipients, setRecipients] = useState<Page<{ openId: string }>>({ list: [], total: 0 });
  const [recipientPage, setRecipientPage] = useState(1);
  const [recipientError, setRecipientError] = useState('');
  const [recipientBusy, setRecipientBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const optionGeneration = useRef(0);
  const searchGeneration = useRef(0);
  const historyGeneration = useRef(0);
  const sending = useRef(false);
  const key = JSON.stringify({ audience, contentType, content });
  const currentKey = useRef(key); currentKey.current = key;
  const disabled = busy || !!pending || !!storageError;

  const loadOptions = async (season?: string) => {
    const generation = ++optionGeneration.current;
    setOptions(undefined); setError('');
    try {
      const data = await read<Options>('/options', season ? { gameSession: season } : {});
      if (generation === optionGeneration.current) setOptions(data);
    } catch (e) { if (generation === optionGeneration.current) setError(errorText(e)); }
  };
  const loadHistory = async (number = page) => {
    const generation = ++historyGeneration.current;
    setHistoryBusy(true); setHistoryError('');
    try {
      const data = await read<Page<HistoryRow>>('/history', { pageNum: number, pageSize: 20 });
      if (generation === historyGeneration.current) setHistory(data);
    } catch (e) { if (generation === historyGeneration.current) setHistoryError(errorText(e)); }
    finally { if (generation === historyGeneration.current) setHistoryBusy(false); }
  };
  useEffect(() => { loadOptions();
    try {
      const value = sessionStorage.getItem(pendingKey());
      if (value) {
        const saved: SendRequest = JSON.parse(value);
        if (!saved.requestId || !saved.audience || typeof saved.content !== 'string') throw new Error('待确认发送记录损坏，请联系管理员核对发送历史后处理');
        setPending(saved); setAudience(saved.audience); setContentType(saved.contentType); setContent(saved.content);
      }
    } catch (e) { setStorageError(errorText(e)); }
    return () => { optionGeneration.current++; searchGeneration.current++; historyGeneration.current++; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadHistory(page); }, [page]);
  useEffect(() => {
    if (!detail) return;
    let active = true; setRecipients({ list: [], total: 0 }); setRecipientError(''); setRecipientBusy(true);
    read<{ list: { openId: string }[]; recipientCount: number }>('/recipients', { messageId: detail.messageId, pageNum: recipientPage, pageSize: 20 })
      .then(data => { if (active) setRecipients({ list: data.list, total: data.recipientCount }); })
      .catch(e => { if (active) setRecipientError(errorText(e)); })
      .finally(() => { if (active) setRecipientBusy(false); });
    return () => { active = false; };
  }, [detail, recipientPage]);
  const changeAudience = (next: Audience) => { setAudience(next); setPreview(undefined); setResult(undefined); };
  const search = async (number = 1) => {
    if (!query.trim()) { setError('请输入搜索条件'); return; }
    const generation = ++searchGeneration.current;
    setError(''); setSearchBusy(true); setUsers([]); setSearched(false);
    try {
      const data = await read<Candidate[]>('/users', { [searchBy]: query.trim(), pageNum: number, pageSize: 20 });
      if (generation === searchGeneration.current) { setUsers(data); setUserPage(number); setSearched(true); }
    } catch (e) { if (generation === searchGeneration.current) setError(errorText(e)); }
    finally { if (generation === searchGeneration.current) setSearchBusy(false); }
  };
  const validation = () => {
    if (!options) return '请先加载通知配置';
    if (audience.type === 'USER' && !audience.openId) return '请搜索并选择接收用户';
    if (['SEASON', 'SEASON_GROUP'].includes(audience.type) && !audience.gameSession) return '请选择赛季';
    if (audience.type === 'SEASON_GROUP' && audience.groupType === undefined) return '请选择分组';
    if (audience.type === 'MEMBER' && !audience.memberScope) return '请选择会员范围';
    if (!content.trim() || content.trim().length > 2000) return '消息内容须为 1～2000 字';
    if (contentType === 2) { try { if (new URL(content.trim()).protocol !== 'https:') return '图片必须使用 HTTPS 地址'; } catch { return '请输入有效的 HTTPS 图片地址'; } }
    return '';
  };
  const doPreview = async () => {
    const message = validation(); if (message) { setError(message); return; }
    const at = key; setBusy(true); setError(''); setPreview(undefined);
    try {
      const data = await write<Preview>('/preview', audience);
      if (at === currentKey.current) { setPreview(data); setPreviewKey(at); }
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  const finish = (data: SendResult) => {
    // If clearing storage fails, keep the immutable request so recovery stays idempotent.
    sessionStorage.removeItem(pendingKey());
    setPending(undefined); setPreview(undefined); setResult(data); setContent(''); setError('');
    setPage(1); loadHistory(1); Message.success(`发送成功，共 ${data.recipientCount} 人`);
  };
  const send = async (request: SendRequest) => {
    if (sending.current) return;
    try { sessionStorage.setItem(pendingKey(), JSON.stringify(request)); }
    catch { setStorageError('浏览器无法保存发送恢复记录，请允许会话存储后重新加载页面。尚未发送消息。'); return; }
    sending.current = true; setBusy(true); setError('');
    setPending(request);
    try {
      const data = await write<SendResult>('/push', request);
      finish(data);
    } catch (e) {
      const code = errorCode(e);
      if (code === 400 || code === 403) {
        sessionStorage.removeItem(pendingKey()); setPending(undefined); setPreview(undefined);
        setError(errorText(e));
      } else setError(`${errorText(e)}。发送结果尚未确认，请查询结果或重试原请求。`);
    } finally { sending.current = false; setBusy(false); }
  };
  const recover = async () => {
    if (!pending || sending.current) return;
    sending.current = true; setBusy(true); setError('');
    try { finish(await read<SendResult>('/result', { requestId: pending.requestId })); }
    catch (e) { setError(errorCode(e) === 404 ? '暂未查到已提交结果，原请求可能仍在处理。请稍后查询，或重试原请求。' : errorText(e)); }
    finally { sending.current = false; setBusy(false); }
  };
  const ready = preview?.canSend && preview.recipientCount > 0 && previewKey === key && !disabled && !uploading && !!options;
  const audienceLabel = audienceDescription({ audienceType: audience.type, audienceParams: JSON.stringify({ ...audience, ...preview?.audience }) });

  return <div className="system-notifications">
    <Alert type="info" content="系统通知按接收人定向发送。先预览人数，再确认发送；发送后不支持撤回。" />
    {error && <Alert type="error" content={error} />}
    {storageError && <Alert type="error" content={storageError} />}
    {!options && <Button disabled={busy} onClick={() => loadOptions(audience.gameSession)}>重新加载通知配置</Button>}
    {pending && <Card title="有一条发送请求待确认">
      <p>{audienceDescription({ audienceType: pending.audience.type, audienceParams: JSON.stringify(pending.audience) })}</p>
      <p>{pending.contentType === 1 ? pending.content : '图片通知'}</p>
      <p>请先确认本次结果，再发送新消息。刷新页面后可恢复本次请求；请勿在结果确认前关闭浏览器标签页。</p>
      <Space><Button loading={busy} onClick={recover}>查询发送结果</Button><Button disabled={busy} onClick={() => send(pending)}>重试原请求</Button></Space>
    </Card>}
    {result && <Alert type="success" content={`发送成功：消息 ${result.messageId}，实际接收 ${result.recipientCount} 人。`} />}
    <Card title="发送系统通知">
      <div className="notification-grid">
        <div>
          <label>接收人范围</label>
          <Select aria-label="接收人范围" value={audience.type} disabled={disabled || !options} onChange={type => {
            searchGeneration.current++; setSearchBusy(false); setSelectedUser(undefined); setUsers([]); setSearched(false);
            changeAudience({ type }); loadOptions();
          }} options={(options?.types || ['USER', 'SEASON', 'SEASON_GROUP', 'MEMBER']).map(type => ({ value: type, label: audienceNames[type] || type }))} />
          {['SEASON', 'SEASON_GROUP'].includes(audience.type) && <>
            <label>赛季</label><Select aria-label="赛季" value={audience.gameSession} disabled={disabled || !options} placeholder="请选择赛季"
              options={options?.seasons.map(s => ({ value: s.gameSession, label: s.gameSessionName || s.gameSession }))}
              onChange={gameSession => { changeAudience({ type: audience.type, gameSession }); loadOptions(gameSession); }} />
            {audience.type === 'SEASON_GROUP' && <><label>分组</label><Select aria-label="分组" value={audience.groupType} placeholder="请选择分组" disabled={disabled || !options || !audience.gameSession}
              options={options?.groups.map(g => ({ value: g.groupType, label: g.groupTypeName }))} onChange={groupType => changeAudience({ ...audience, groupType })} /></>}
            {['SEASON', 'SEASON_GROUP'].includes(audience.type) && <><label>是否付费</label><Select aria-label="是否付费" value={audience.paymentScope || 'ALL'} disabled={disabled || !options || !audience.gameSession || (audience.type === 'SEASON_GROUP' && audience.groupType === undefined)}
              options={(['ALL', 'FREE', 'PAID'] as const).map(value => ({ value, label: paymentNames[value] }))}
              onChange={paymentScope => changeAudience({ ...audience, paymentScope })} /></>}
          </>}
          {audience.type === 'MEMBER' && <><label>会员范围</label><Select aria-label="会员范围" disabled={disabled} value={audience.memberScope} options={options?.memberScopes.map(value => ({ value, label: memberNames[value] || value }))} onChange={memberScope => changeAudience({ ...audience, memberScope })} /></>}
          {audience.type === 'USER' && <>
            <label>查找用户</label><Space wrap>
              <Select aria-label="搜索方式" style={{ width: 130 }} value={searchBy} disabled={disabled || searchBusy} onChange={value => { searchGeneration.current++; setSearchBy(value); setUsers([]); setSearched(false); }} options={[{ value: 'gameName', label: '完整参赛名' }, { value: 'phone', label: '完整手机号' }, { value: 'openId', label: 'openId' }]} />
              <Input aria-label="用户搜索条件" value={query} disabled={disabled || searchBusy} placeholder={searchBy === 'gameName' ? '输入完整参赛名（非包含搜索）' : searchBy === 'phone' ? '输入完整手机号' : '输入完整 openId'} onChange={value => { searchGeneration.current++; setQuery(value); setUsers([]); setSearched(false); }} onPressEnter={() => search()} />
              <Button disabled={disabled || !options} loading={searchBusy} onClick={() => search()}>搜索用户</Button>
            </Space>
            {searched && <><Table size="small" rowKey="openId" pagination={false} data={users} columns={[
              { title: '昵称', dataIndex: 'gameName' }, { title: '手机号', dataIndex: 'maskedPhone' }, { title: 'ID 尾号', dataIndex: 'openIdSuffix' },
              { title: '操作', render: (_, row: Candidate) => <Button size="small" disabled={disabled} onClick={() => { setSelectedUser(row); changeAudience({ type: 'USER', openId: row.openId }); }}>选择</Button> }
            ]} /><Space><Button disabled={disabled || searchBusy || userPage === 1} onClick={() => search(userPage - 1)}>上一页</Button><span>第 {userPage} 页</span><Button disabled={disabled || searchBusy || users.length < 20} onClick={() => search(userPage + 1)}>下一页</Button></Space></>}
            {selectedUser && <Alert type="success" content={`已选择：${selectedUser.gameName || '未命名'} · ${selectedUser.maskedPhone} · ID 尾号 ${selectedUser.openIdSuffix}`} />}
          </>}
        </div>
        <div>
          <label>消息内容</label><Radio.Group value={contentType} disabled={disabled || uploading} onChange={type => { setContentType(type); setContent(''); setPreview(undefined); }} options={[{ label: '文本', value: 1 }, { label: '图片', value: 2 }]} />
          {contentType === 1 ? <Input.TextArea aria-label="通知正文" value={content} disabled={disabled} onChange={setContent} maxLength={2000} showWordLimit autoSize={{ minRows: 5, maxRows: 12 }} placeholder="请输入通知正文" /> : <>
            <Input aria-label="图片地址" value={content} disabled={disabled || uploading} placeholder="HTTPS 图片地址，或上传图片" onChange={setContent} />
            <Upload key={contentType} action={baseUrl + '/common/uploadFile'} accept="image/*" showUploadList={false} disabled={disabled || uploading} headers={{ Authorization: localStorage.getItem('authToken') || '' }}
              onChange={(_, file) => {
                setUploading(file.status === 'uploading');
                if (file.status === 'done') {
                  const response = file.response as { code?: number; data?: string; message?: string } | undefined;
                  if (response?.code === 200 && typeof response.data === 'string') { setContent(response.data); setPreview(undefined); }
                  else setError(response?.message || '图片上传失败');
                }
                if (file.status === 'error') setError('图片上传失败，请重试');
              }}><Button disabled={disabled || uploading}>{uploading ? '上传中…' : '上传图片'}</Button></Upload>
            {/^https:\/\//i.test(content) && <Image src={content} width={120} height={120} />}
          </>}
          <div className="notification-actions"><Space wrap>
            <Button loading={busy && !pending} disabled={disabled || uploading || !options} onClick={doPreview}>预览接收人数</Button>
            <Button type="primary" disabled={!ready} onClick={() => setConfirming(true)}>确认发送</Button>
          </Space></div>
          {preview && previewKey === key ? <Alert type={preview.canSend ? 'info' : 'warning'} content={`${audienceLabel}：预计 ${preview.recipientCount} 人。${preview.reason || '最终人数以发送时筛选结果为准。'}`} /> : <p className="notification-help">选择接收人并填写内容后，点击预览。修改内容或接收条件后需重新预览。</p>}
        </div>
      </div>
    </Card>
    <Card title="发送历史" extra={<Button onClick={() => loadHistory()} loading={historyBusy}>刷新历史</Button>}>
      {historyError && <Alert type="error" content={historyError} />}
      <Table rowKey="messageId" loading={historyBusy} data={history.list} scroll={{ x: 950 }} pagination={{ current: page, pageSize: 20, total: history.total, showTotal: true, onChange: setPage }} columns={[
        { title: '消息 ID', dataIndex: 'messageId', width: 90 },
        { title: '消息内容', width: 260, render: (_, row: HistoryRow) => row.contentType === 2 ? <Image src={row.content} width={52} height={52} /> : <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{row.content}</div> },
        { title: '接收范围', render: (_, row: HistoryRow) => audienceDescription(row) },
        { title: '人数', dataIndex: 'recipientCount', width: 80 }, { title: '发送人', dataIndex: 'operatorName', width: 100 },
        { title: '发送时间', dataIndex: 'sendTime', width: 185, render: value => String(value || '').replace('T', ' ') },
        { title: '操作', width: 110, render: (_, row: HistoryRow) => <Button type="text" onClick={() => { setRecipientPage(1); setDetail(row); }}>接收明细</Button> }
      ]} />
    </Card>
    <Modal title="确认发送系统通知" visible={confirming} onCancel={() => setConfirming(false)} onOk={() => {
      setConfirming(false);
      if (ready) send({ requestId: requestId(), audience: { ...audience }, contentType, content: content.trim() });
    }} okButtonProps={{ disabled: !ready }} okText="发送通知">
      <p>{audienceLabel}，预计 {preview?.recipientCount || 0} 人。</p>
      {contentType === 1 ? <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{content}</p> : <Image src={content} width={180} />}
      <Alert type="warning" content="发送后无法撤回。最终接收名单以发送时生成的快照为准。" />
    </Modal>
    <Modal title={`消息 ${detail?.messageId || ''} 的接收明细`} visible={!!detail} footer={null} onCancel={() => setDetail(undefined)}>
      {recipientError && <Alert type="error" content={recipientError} />}
      <p>实际接收 {detail?.recipientCount || 0} 人</p>
      <Table rowKey="openId" loading={recipientBusy} data={recipients.list} columns={[{ title: '接收用户 openId', dataIndex: 'openId' }]} pagination={{ current: recipientPage, pageSize: 20, total: recipients.total, onChange: setRecipientPage }} />
    </Modal>
  </div>;
}